import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query'
import { cancelOutboundCutoff, getOutbounds, getOutboundSummary } from './apis'
import {
  GetOutboundsDownload,
  GetOutboundsReq,
  GetOutboundSummaryReq,
  OutboundsItem,
  OutboundsMenuCode,
} from '@/services/outbound/outboundSearch/types'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { Utils } from '@/utils/Utils'
import { AgGridRowId } from '@/services/common/types'
import { ExcelDownloadType } from '@/services/excel/excel.types'
import { menuCodeHeaders } from '@/utils/menuCodeHeaders'

const PATHS = ['OUTBOUND', 'SEARCH', 'BATCH']
const GET_OUTBOUND_SUMMARY = [...PATHS, 'GET_OUTBOUND_SUMMARY', 'gi24']
const GET_OUTBOUNDS_BATCH = [...PATHS, 'GET_OUTBOUNDS_BATCH', 'gi25']
const CANCEL_OUTBOUND_CUTOFF = [...PATHS, 'CANCEL_OUTBOUND_CUTOFF']

// 출고조회 엑셀 다운로드 API
const EXCEL_API_OUTBOUNDS = '/api/v1/get-outbounds/download'

export const useOutboundSummary = (params: ApiPaginationReq<GetOutboundSummaryReq>) => {
  return useQuery({
    queryKey: [...GET_OUTBOUND_SUMMARY, params],
    queryFn: () => getOutboundSummary(params),
    select: ({ data }) => {
      return { ...data, items: Utils.reformatArrayToAddIds(data.items) }
    },
    enabled: false,
  })
}

export const useGetOutbounds = (
  params: ApiPaginationReq<GetOutboundsReq> & Partial<AgGridRowId>,
  menuCode: OutboundsMenuCode,
): UseQueryResult<ApiPageResponse<OutboundsItem>, Error> => {
  // TODO: [SCOPE B] 아래 다건 입력 구조 데이터 타입을 배열 -> 스트링으로 변환 필요 (API 수정 후 아래 코드 삭제 필요)
  const searchParams = {
    ...params,
    orderNumbers: typeof params.orderNumbers === 'string' ? [params.orderNumbers] : params.orderNumbers,
    orderOptionNumbers: typeof params.orderOptionNumbers === 'string' ? [params.orderOptionNumbers] : params.orderOptionNumbers,
    orderConsolNumbers: typeof params.orderConsolNumbers === 'string' ? [params.orderConsolNumbers] : params.orderConsolNumbers,
    uids: typeof params.uids === 'string' ? [params.uids] : params.uids,
    barcodes: typeof params.barcodes === 'string' ? [params.barcodes] : params.barcodes,
    invoiceNumbers: typeof params.invoiceNumbers === 'string' ? [params.invoiceNumbers] : params.invoiceNumbers,
    salChannels: typeof params.salChannels === 'string' ? [params.salChannels] : params.salChannels,
  }

  return useQuery({
    // 화면코드를 키에 둔다 — 두 화면이 캐시를 같이 쓰면 다른 화면 코드로 재조회된다.
    queryKey: [...GET_OUTBOUNDS_BATCH, searchParams, menuCode],
    queryFn: () => getOutbounds(searchParams, menuCode),
    select: ({ data }) => {
      return { ...data, items: Utils.reformatArrayToAddIds(data.items) }
    },
    enabled: !!params.id,
  })
}

export const useExportExcelOnOutbounds = (params: GetOutboundsDownload, menuCode: OutboundsMenuCode) => {
  const excelDownload = useExcelDownload({ config: { method: 'post', ...menuCodeHeaders(menuCode) } })
  // TODO: [SCOPE B] 아래 다건 입력 구조 데이터 타입을 배열 -> 스트링으로 변환 필요 (API 수정 후 아래 코드 삭제 필요)
  const searchParams = {
    ...params,
    orderNumbers: typeof params.orderNumbers === 'string' ? [params.orderNumbers] : params.orderNumbers,
    orderOptionNumbers: typeof params.orderOptionNumbers === 'string' ? [params.orderOptionNumbers] : params.orderOptionNumbers,
    orderConsolNumbers: typeof params.orderConsolNumbers === 'string' ? [params.orderConsolNumbers] : params.orderConsolNumbers,
    uids: typeof params.uids === 'string' ? [params.uids] : params.uids,
    barcodes: typeof params.barcodes === 'string' ? [params.barcodes] : params.barcodes,
    invoiceNumbers: typeof params.invoiceNumbers === 'string' ? [params.invoiceNumbers] : params.invoiceNumbers,
    salChannels: typeof params.salChannels === 'string' ? [params.salChannels] : params.salChannels,
    shipperNames: typeof params.shipperNames === 'string' ? [params.shipperNames] : params.shipperNames,
    logisticsServiceTypes: typeof params.logisticsServiceTypes === 'string' ? [params.logisticsServiceTypes] : params.logisticsServiceTypes,
  }

  return {
    isLoading: excelDownload.isLoading,
    onExportData: (additionalParams?: ExcelDownloadType) => {
      excelDownload.onDownloadExcel(EXCEL_API_OUTBOUNDS, {
        ...searchParams,
        ...additionalParams,
      })
    },
  }
}

export const useCancelOutboundCutoff = () => {
  return useMutation({
    mutationKey: CANCEL_OUTBOUND_CUTOFF,
    mutationFn: (cutOffNo: string) => cancelOutboundCutoff(cutOffNo),
  })
}
